import React, { FC, MouseEvent, useMemo, useRef, useState } from 'react'

import clsx from 'clsx'
import { useClickAway } from 'react-use'
import styled from 'styled-components'

import { Txt } from 'src/components/atoms'
import { BoldSubstrings } from 'src/components/atoms/texts/BoldSubstrings'
import { ProjectStatusTxt } from 'src/components/molecules'

import { useGetProjects } from 'src/hooks'
import { hasElementInId } from 'src/libs/helper'
import { ProjectEntity } from 'src/types'

const Container = styled.div`
  width: 100%;
  min-height: 3.57rem;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.05);
  max-height: 37.5rem;
  padding: 0.86rem;
  transition: all 0.1s ease-in-out;
  position: relative;
  display: flex;
  flex-wrap: wrap;

  &:hover:not(:focus):not(:disabled) {
    box-shadow: inset 0 0 8px rgba(0, 0, 0, 0.1);
  }
`

const ContentInput = styled.input`
  outline: none;
  resize: none;
  height: 2em;
  line-height: 2em;
  width: 100%;
`

const SearchedContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  transform: translateY(100%);
  z-index: 100;
  filter: drop-shadow(0px 5px 20px rgba(122, 137, 160, 0.1));
  width: 100%;
`

const ProjectList = styled.ul`
  border-radius: 0 0 8px 8px;
  overflow: auto;
  max-height: 21.43rem;
`

type Props = {
  alreadyAssignedProjectIds: string[]
  onSelectProject: (project: ProjectEntity) => void
  className?: string
}

export const AssigningProjectSearch: FC<Props> = ({
  alreadyAssignedProjectIds,
  onSelectProject,
  className,
}) => {
  const SearchInputId = 'project-search-input'

  const [inputText, setInputText] = useState<string>('')
  const [showSearchProjects, setShowSearchProjects] = useState(false)

  const searchWords = useMemo(() => inputText.split(/[\s,]/).filter(Boolean), [inputText])

  const { projects } = useGetProjects()

  const targetProjects: ProjectEntity[] = useMemo(() => {
    return projects
      .filter(project => !project.isDeleting)
      .filter(project =>
        alreadyAssignedProjectIds.every(assignedProjectId => assignedProjectId !== project.id)
      )
      .filter(project => {
        // テキストが未入力の場合は全件対象
        if (!inputText) {
          return true
        }

        // lowerCaseに変換して調べられるようにする
        return searchWords.some(word => project.name.toLowerCase().includes(word.toLowerCase()))
      })
  }, [alreadyAssignedProjectIds, inputText, projects, searchWords])

  const handleOnSelectTargetProject = (event: MouseEvent, project: ProjectEntity) => {
    event.stopPropagation()
    onSelectProject(project)
  }

  const handleOnFocusInput = () => {
    setShowSearchProjects(true)
  }

  const ref = useRef(null)
  // 検索結果の外をクリックした時
  useClickAway(ref, event => {
    // クリックした要素が検索を行うためのinputだった場合は何もしない
    const target = event.target as HTMLElement
    if (target && hasElementInId(target, SearchInputId)) {
      return
    }

    if (showSearchProjects) {
      setShowSearchProjects(false)
    }
  })

  return (
    <div className={className}>
      <div className="tw-relative">
        <Container
          className={clsx(
            'tw-bg-surface-primary',
            'tw-border-y has-[input:focus]:tw-border',
            'tw-border-border-secondary has-[input:focus]:tw-border-roovPrimary-default'
          )}
        >
          <ContentInput
            id={SearchInputId}
            placeholder="プロジェクト名称で検索（複数可）"
            value={inputText}
            onChange={event => setInputText(event.target.value)}
            onFocus={handleOnFocusInput}
            className="tw-caret-correctGreen-default"
          />
        </Container>

        {showSearchProjects && (
          <SearchedContainer>
            <ProjectList ref={ref} className="tw-border-border-secondary">
              {targetProjects.map(project => (
                <li
                  key={project.id}
                  role="presentation"
                  onClick={event => handleOnSelectTargetProject(event, project)}
                  className="tw-border-b tw-border-b-border-secondary last:tw-border-0"
                >
                  <button
                    type="button"
                    className="tw-w-full tw-h-full tw-flex tw-full-justify tw-p-[1.14rem] tw-bg-surface-primary hover:tw-bg-surface-secondary tw-gap-s-2"
                  >
                    <Txt className="tw-text-left tw-break-all">
                      <BoldSubstrings substrings={searchWords ?? []}>{project.name}</BoldSubstrings>
                    </Txt>
                    <ProjectStatusTxt
                      status={project.status}
                      className="tw-font-bold tw-shrink-0"
                    />
                  </button>
                </li>
              ))}
            </ProjectList>
          </SearchedContainer>
        )}
      </div>
    </div>
  )
}
